// Global Instructions Rule Applied!
//
// Period Highlights list — orchestration (service → local state → page), the
// useLpAllocation shape: filters, one sort key, server paging, options.
import { useCallback, useEffect, useState } from 'react'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import {
  HighlightSetFilterOptions,
  HighlightSetRecord
} from '../../../state_manager/data_models/period_highlights_model.ts'
import {
  fetchHighlightSetFilterOptions,
  fetchHighlightSets,
  HighlightSetsQuery
} from '../services/periodHighlightsService.ts'

export interface PeriodHighlightsFilters {
  scopeType: string | null
  statuses: string[]
  fundIds: string[]
  periodEnd: string | null
  currentOnly: boolean
}

export const EMPTY_PERIOD_HIGHLIGHTS_FILTERS: PeriodHighlightsFilters = {
  scopeType: 'fund',
  statuses: [],
  fundIds: [],
  periodEnd: null,
  currentOnly: false
}

const EMPTY_OPTIONS: HighlightSetFilterOptions = {
  highlight_sets__scope_type: [],
  highlight_sets__status: [],
  highlight_sets__fund_id: [],
  highlight_sets__account_id: [],
  highlight_sets__period_end: []
}

const toQueryFilters = (f: PeriodHighlightsFilters): HighlightSetsQuery['filters'] => {
  const out: Array<{ key: string; value: unknown }> = []
  if (f.scopeType) out.push({ key: 'highlight_sets__scope_type', value: f.scopeType })
  if (f.statuses.length) out.push({ key: 'highlight_sets__status', value: f.statuses })
  if (f.fundIds.length) out.push({ key: 'highlight_sets__fund_id', value: f.fundIds })
  if (f.periodEnd) out.push({ key: 'highlight_sets__period_end', value: f.periodEnd })
  if (f.currentOnly) out.push({ key: 'prog__current_only', value: true })
  return out
}

const token = () => getAccessToken(store.getState().auth)

export const usePeriodHighlights = () => {
  const [rows, setRows] = useState<HighlightSetRecord[]>([])
  const [count, setCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState<PeriodHighlightsFilters>(EMPTY_PERIOD_HIGHLIGHTS_FILTERS)
  const [sorting, setSorting] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'highlight_sets__period_end', direction: 'desc' })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [options, setOptions] = useState<HighlightSetFilterOptions>(EMPTY_OPTIONS)

  const refetch = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchHighlightSets(
        { filters: toQueryFilters(filters), sorting: [sorting], page, page_size: pageSize },
        token()
      )
      setRows(result.data)
      setCount(result.count)
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to load period highlights')
    } finally {
      setLoading(false)
    }
  }, [filters, sorting, page, pageSize])

  const refetchOptions = useCallback(async () => {
    try {
      setOptions(await fetchHighlightSetFilterOptions(token()))
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to load filter options')
    }
  }, [])

  useEffect(() => { refetch() }, [refetch])
  useEffect(() => { refetchOptions() }, [refetchOptions])

  return { rows, count, loading, filters, setFilters, sorting, setSorting, page, setPage, pageSize, setPageSize, options, refetch, refetchOptions }
}
